import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modale "Email de proposition" pour UN bien précis affecté à un contact.
 * - Génère un email IA spécifique au bien (POST /contacts/{id}/generate-email?bien_id=...)
 * - Permet d'éditer, copier, historiser et envoyer (Resend) au contact.
 */
const ProposeBienEmailModal = ({ contactId, contactName, contactEmail, bienId, bienReference, onClose }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [generating, setGenerating] = useState(true);
  const [sending, setSending] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token') || '';
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, []);

  const generate = useCallback(async () => {
    setGenerating(true);
    try {
      const url = `${API_URL}/api/contacts/${contactId}/generate-email?email_type=proposition${bienId ? `&bien_id=${bienId}` : ''}`;
      const res = await fetch(url, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (data?.email) {
        setSubject(data.email.subject || '');
        setBody(data.email.body || '');
      } else {
        toast.error("Impossible de générer l'email");
      }
    } catch (e) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  }, [contactId, bienId, authHeaders]);

  useEffect(() => {
    generate();
    // Vérifier si l'envoi Resend est configuré
    fetch(`${API_URL}/api/email/status`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setEmailConfigured(!!d?.configured))
      .catch(() => setEmailConfigured(false));
  }, [generate, authHeaders]);

  const handleCopy = () => {
    navigator.clipboard.writeText(`Objet : ${subject}\n\n${body}`);
    toast.success('Email copié dans le presse-papier');
  };

  const handleHistorize = async () => {
    try {
      const res = await fetch(`${API_URL}/api/contacts/${contactId}/save-email-to-history`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ email_type: 'proposition', subject, body }),
      });
      if (res.ok) toast.success('Email enregistré dans l\'historique');
      else toast.error("Échec de l'enregistrement");
    } catch {
      toast.error("Échec de l'enregistrement");
    }
  };

  const handleSend = async () => {
    if (!contactEmail) {
      toast.error("Ce contact n'a pas d'email renseigné");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/email/send`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ to: [contactEmail], subject, body, contact_id: contactId }),
      });
      if (res.ok) {
        toast.success(`Email envoyé à ${contactEmail}`);
        onClose?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Échec de l'envoi");
      }
    } catch {
      toast.error("Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
      onClick={onClose}
      data-testid="propose-email-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header (IA = violet) */}
        <div className="bg-gradient-to-r from-violet-600 to-violet-700 text-white px-5 py-4 rounded-t-xl flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold flex items-center gap-2">
              <i className="bi bi-stars"></i> Email de proposition (IA)
            </h3>
            <p className="text-violet-100 text-xs mt-0.5 truncate">
              {contactName} · Bien <span className="font-semibold num">{bienReference}</span>
            </p>
          </div>
          <button onClick={onClose} data-testid="propose-email-close" className="text-white/90 hover:bg-white/20 rounded-lg p-1.5">
            <i className="bi bi-x-lg text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {generating ? (
            <div className="text-center py-12 text-slate-500">
              <i className="bi bi-stars animate-pulse text-2xl text-violet-500"></i>
              <p className="text-sm mt-3">Génération de l'email pour ce bien…</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1.5">OBJET</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="propose-email-subject"
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg focus:border-violet-500 outline-none font-semibold text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1.5">CORPS DE L'EMAIL</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={13}
                  data-testid="propose-email-body"
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg focus:border-violet-500 outline-none text-sm leading-relaxed resize-none"
                />
              </div>
              {contactEmail ? (
                <p className="text-xs text-slate-500">
                  <i className="bi bi-envelope"></i> Destinataire : <span className="font-semibold">{contactEmail}</span>
                </p>
              ) : (
                <p className="text-xs text-amber-600">
                  <i className="bi bi-exclamation-triangle"></i> Aucun email renseigné pour ce contact — l'envoi direct est désactivé.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2 rounded-b-xl">
          <button
            onClick={generate}
            disabled={generating}
            data-testid="propose-email-regenerate"
            className="px-3 py-2 text-sm font-medium text-violet-700 hover:bg-violet-100 rounded-lg disabled:opacity-50 flex items-center gap-1.5"
          >
            <i className="bi bi-arrow-repeat"></i> Régénérer
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              disabled={generating}
              data-testid="propose-email-copy"
              className="px-3 py-2 text-sm font-medium text-slate-700 hover:bg-white border border-slate-200 rounded-lg disabled:opacity-50"
            >
              <i className="bi bi-clipboard"></i> Copier
            </button>
            <button
              onClick={handleHistorize}
              disabled={generating}
              data-testid="propose-email-historize"
              className="px-3 py-2 text-sm font-medium text-slate-700 hover:bg-white border border-slate-200 rounded-lg disabled:opacity-50"
            >
              <i className="bi bi-archive"></i> Historiser
            </button>
            {emailConfigured && (
              <button
                onClick={handleSend}
                disabled={generating || sending || !contactEmail}
                data-testid="propose-email-send"
                className="px-4 py-2 bg-[#0062CC] hover:bg-[#0056b3] disabled:bg-slate-300 text-white rounded-lg text-sm font-semibold flex items-center gap-2"
              >
                <i className={`bi ${sending ? 'bi-arrow-repeat animate-spin' : 'bi-send'}`}></i>
                {sending ? 'Envoi…' : 'Envoyer'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposeBienEmailModal;
